import React from 'react';
import styled from '~/utils/styled';
// ---- external imports ----

import Asset from '~/containers/GameAsset';
import { TextCursor, DeniedCursor } from '~/containers/GameAsset/cursors';
import btnStartImg from '~/assets/images/auth/btn-start.png';
import modalLoginImg from '~/assets/images/auth/modal-login.png';
import eyeActiveImg from '~/assets/images/auth/eye-active.png';
import eyeInactiveImg from '~/assets/images/auth/eye-inactive.png';
// ---- internal imports ----

// region [Projection Constants]
export const FORM_WIDTH_PROJECTION = { width: 632 };
// endregion

// region [Components]

// Modal panel — identical across all 5 auth screens
export const ModalBackground = Asset.Img.withProjection(modalLoginImg);

// Eye toggle — shared across Login, Register, ResetPassword
// Accepts `isActive`; resolves image internally so screens never import eye images
const BaseEyeButton = styled(Asset.Button)`
	background-color: transparent;
	border: none;
	padding: 0;
`;
export function EyeButton({ isActive, ...props }) {
	return <BaseEyeButton projection={isActive ? eyeActiveImg : eyeInactiveImg} {...props} />;
}

// Title heading — default: fontSize=128, lineHeight=128, projection=FORM_WIDTH_PROJECTION
// Screens with different values pass overrides at the JSX call site
export const TitleText = styled(Asset.Text.withProps({
	fontSize: 128,
	lineHeight: 128,
	projection: FORM_WIDTH_PROJECTION,
}))`
	font-family: "LuckiestGuy";
	color: #fff5b0;
	text-shadow: -4px 2px 0 #4d2f1c;
	text-align: center;
`;

// Field label — default: fontSize=32, projection=FORM_WIDTH_PROJECTION
// Register passes fontSize={16} at the JSX call site
export const FieldLabel = styled(Asset.Text.withProps({
	fontSize: 32,
	projection: FORM_WIDTH_PROJECTION,
}))`
	font-family: "NotoSans Semibold";
	color: #fff5b0;
`;

// Error text — identical across Login, Register, ForgotPassword, ResetPassword
export const ErrorMessage = styled(Asset.Text.withProps({
	fontSize: 20,
	projection: FORM_WIDTH_PROJECTION,
}))`
	font-family: "NotoSans";
	color: #FFF5B0;
`;

// Input background — identical CSS; projection always varies by screen → no withProps
// All callers pass projection explicitly at the JSX call site
export const InputBackground = styled(Asset.Backdrop)`
	background-color: rgba(0, 0, 0, 0.1);
	border-radius: 12px;
	box-shadow: inset 0 0 17px 0 rgba(0, 0, 0, 0.25);
`;

// Field input — padding for inputs with an eye button overlay (right clearance)
// Screens without eye button (Register full-fields, ForgotPassword) extend with padding override
export const FieldInput = styled(Asset.Input.withProps({
	fontSize: 32,
}))`
	font-family: "NotoSans SemiBold";
	color: #fff5b0;
	padding: 0 48px 0 16px;
	border-radius: 12px;
	box-sizing: border-box;
	${TextCursor};

	::placeholder {
		color: rgba(114, 37, 12, 1);
		font-family: 'NotoSans SemiBold'
	};

	&:focus {
		border-color: rgba(255, 245, 176, 1);
		box-shadow: inset 0 0 17px 0 rgba(0, 0, 0, 0.25), 0 0 12px 0 rgba(255, 245, 176, 1);
		outline: none;
	};

	&:disabled {
		${DeniedCursor};
		background-color: rgba(32, 32, 32, 0.4);
	};

	${({ hasError }) => hasError && `
		border: 4px solid #D63B25;
		box-shadow: inset 0 0 17px 0 rgba(0, 0, 0, 0.25);
	`}
`;

// The primary action button — Start, Reset, Request Link, Try Again. One asset
// and one label style, written out separately on five auth screens before this.
export const PrimaryButton = styled(Asset.Button.withProjection(btnStartImg))`
	border: none;
	padding: 0;
`;

// The label's projection is its own width, not the button's — it centres itself
// inside the button, and a short label sits left of centre otherwise. That width
// is the only thing that differs per screen, so each screen builds its label
// once at module scope from its own projection.
export function createPrimaryButtonText(projection) {
	return styled(Asset.Text.withProps({
		fontSize: 24,
		projection,
	}))`
		font-family: "NotoSans ExtraBold";
		color: #4D2F1C;
		text-align: center;
		pointer-events: none;
	`;
}

// endregion
